// src/pages/MallManagementEasyMall.js
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Stack,
  Button,
  Typography,
  Grid,
  Card,
  Paper,
  TextField,
  InputAdornment,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  Pagination,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import Iconify from 'src/components/iconify';
import ReactApexChart from 'react-apexcharts';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { SERVICES } from 'src/utils/servicePermissions';
import useServicePermission from 'src/hooks/useServicePermission';

export default function MallManagementEasyMall() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Check service permission using hook
  const { hasPermission: easyMallPermission, loading: permissionLoading } = useServicePermission(SERVICES.EASY_MALL);

  // Badge in the top‐right of each chart card
  // Metric card data
  const dailyMetric = {
    title: 'Günlük Kullanım Adedi',
    total: 1248,
    average: 712,
  };
  const renderBadge = () => (
    <Box
      position="absolute"
      top={16}
      right={16}
      display="flex"
      alignItems="center"
    >
      <Iconify
        icon="eva:arrow-up-fill"
        width={16}
        height={16}
        sx={{ color: theme.palette.success.main }}
      />
      <Typography
        variant="subtitle2"
        sx={{ color: theme.palette.success.main, ml: 0.5 }}
      >
        %22
      </Typography>
    </Box>
  );

  //
  // — Metric card data
  //
  // (we only need one metric for this page)
  //
  // — Chart 1: Aylık Teslimat Tercihine Göre Kullanımı (stacked bar)

  const commonBarPlot = {
    bar: {
      columnWidth: '20%',
      borderRadius: 8, // üst köşeleri yuvarlatır
      borderRadiusApplication: 'end', // yalnızca üst kenarları yuvarla (ApexCharts 3.35+)
    },
  };

  const deskBarPlot = {
    bar: {
      columnWidth: '45%',
      borderRadius: 8, // üst köşeleri yuvarlatır
      borderRadiusApplication: 'end', // yalnızca üst kenarları yuvarla (ApexCharts 3.35+)
    },
  };

  const monthlyRangeSeries = [
    { name: 'Çıkış', data: [700, 650, 900, 400] },
    { name: 'Kozmetik', data: [600, 550, 800, 450] },
    { name: 'Elektronik', data: [500, 450, 700, 350] },
  ];
  const monthlyRangeOptions = {
    chart: { type: 'bar', stacked: true, toolbar: { show: false } },
    plotOptions: commonBarPlot,
    colors: ['#1C64F2', '#16BDCA', '#FDBA8C'],
    xaxis: {
      categories: ['Jan 0-7', 'Jan 7-14', 'Jan 14-21', 'Jan 21-30'],
    },
    legend: { position: 'bottom' },
    yaxis: { title: { text: 'Toplam Taşıma Adedi' } },
    tooltip: { shared: true, intersect: false },
    dataLabels: { enabled: false }
  };

  // — Chart 2: Aylık Kullanım Adedi & Ciro (column + line)
  const monthlyComboSeries = [
    { name: 'Kullanım Adedi', type: 'column', data: [1800, 1500, 2000, 1700] },
    { name: 'Ciro', type: 'line', data: [900, 1200, 800, 1300] },
  ];
  const monthlyComboOptions = {
    chart: { stacked: false, toolbar: { show: false } },
    stroke: { width: [0, 2] },
    plotOptions: commonBarPlot,
    colors: ['#1C64F2', '#16BDCA'],
    fill: { opacity: [0.85, 1] },
    xaxis: {
      categories: ['Jan 0-7', 'Jan 7-14', 'Jan 14-21', 'Jan 21-30'],
    },
    yaxis: [
      { title: { text: 'Toplam Kullanım Adedi' } },
      {
        opposite: true,
        title: { text: 'Toplam Ciro' },
      },
    ],
    legend: { position: 'bottom' },
  };

  // — Chart 3: Haftalık Easy Mall Hizmeti Kullanımı (donut)
  const weeklyDonutSeries = [25.6, 20.4, 21.8, 18.5];
  const weeklyDonutOptions = {
    chart: { type: 'donut', toolbar: { show: false } },
    labels: ['Otel', 'Emanet Dolabı', 'Rezidans', 'Easy Point'],
    colors: ['#1C64F2', '#16BDCA', '#FDBA8C', '#E74694'],
 plotOptions: {
      pie: {
        donut: {
          size: '65%',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '15px',
              color: '#6B7280',
              fontWeight:'400',
              offsetY: 25, // label'ı alta kaydır
              formatter: function (val, opts) {
                return val; // buraya label
              },
            },
            value: {
              show: true,
              fontSize: '30px',
              fontWeight: '700',
              offsetY: -12,
              color: '#111',
              formatter: (val) => `% ${val}`, // örnek özel format
            },
            total: {
              show: false,
              label: 'Toplam',
              fontSize: '14px',
              color: '#999',
              formatter: (w) => {
                const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                return `${total}`;
              },
            },
          },
        },
      },
    },    dataLabels: { enabled: false },
    legend: { position: 'bottom', horizontalAlign: 'center' },
  };

  // — Chart 4: Haftalık Kullanım Adedi & Ciro Hareketleri (column + line)
  const weeklyComboSeries = [
    {
      name: 'Kullanım Adedi',
      type: 'column',
      data: [1500, 1700, 1400, 1900, 1600, 1750, 1550],
    },
    {
      name: 'Ciro',
      type: 'line',
      data: [1000, 1200, 900, 1300, 1100, 1250, 1150],
    },
  ];
  const weeklyComboOptions = {
    chart: { stacked: false, toolbar: { show: false } },
    stroke: { width: [0, 2] },
    plotOptions:deskBarPlot,
    colors: ['#2563EB', '#14B8A6'],
    fill: { opacity: [0.85, 1] },
    xaxis: {
      categories: [
        'Jan 11',
        'Jan 12',
        'Jan 13',
        'Jan 14',
        'Jan 15',
        'Jan 16',
        'Jan 17',
      ],
    },
    legend: { position: 'bottom' },
  };

  // — Chart 5: Saatlik Kullanım Trafiği ve Satış Hareketleri (line & line)
  const bottomLineSeries = [
    { name: 'Satış', data: [9, 12, 14, 11, 13, 12, 10] },
    { name: 'Trafik', data: [10, 11, 13, 10, 12, 11, 9] },
  ];
  const bottomLineOptions = {
    chart: { toolbar: { show: false } },
    stroke: { curve: 'smooth', width: [3, 3] },
    colors: ['#2563EB', '#14B8A6'],
    markers: { size: 4 },
    grid: {
      borderColor: theme.palette.divider,
      strokeDashArray: 4,
      yaxis: { lines: { show: true } },
    },
    xaxis: {
      categories: [
        'P.tesi',
        'Salı',
        'Çarş.',
        'Perş.',
        'Cuma',
        'C.tesi',
        'Pazar',
      ],
      axisBorder: { show: false },
    },
    yaxis: {
      min: 9,
      max: 15,
      tickAmount: 3,
      labels: {
        formatter: (val) => `${Math.round(val)}:00`,
        style: { color: theme.palette.text.secondary },
      },
    },
    tooltip: { shared: true, intersect: false },
    legend: { show: false },
  };

  //
  // — Table state
  //
  const [startDate, setStartDate] = useState(moment());
  const [endDate, setEndDate] = useState(moment());

  const rows = [
    {
      no: 1,
      avm: 'Zorlu Center',
      konsinyer: 'Yük',
      sistem: 'Var',
      konsinyerKabul: '07.04.2025 17:12',
      sistemKabul: '07.04.2025 17:13',
    },
    {
      no: 2,
      avm: 'Zorlu Center',
      konsinyer: 'Yük',
      sistem: 'Var',
      konsinyerKabul: '07.04.2025 17:12',
      sistemKabul: '07.04.2025 17:13',
    },
    {
      no: 3,
      avm: 'Zorlu Center',
      konsinyer: 'Yük',
      sistem: 'Var',
      konsinyerKabul: '07.04.2025 17:12',
      sistemKabul: '07.04.2025 17:13',
    },
    {
      no: 4,
      avm: 'Zorlu Center',
      konsinyer: 'Yük',
      sistem: 'Var',
      konsinyerKabul: '07.04.2025 17:12',
      sistemKabul: '07.04.2025 17:13',
    },
    {
      no: 5,
      avm: 'Zorlu Center',
      konsinyer: 'Yük',
      sistem: 'Var',
      konsinyerKabul: '07.04.2025 17:12',
      sistemKabul: '07.04.2025 17:13',
    },
  ];

  return (
    <Box sx={{ px: 4, py: 3 }}>
      <Helmet>
        <title>Easy Mall</title>
      </Helmet>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Typography variant="h5">Easy Mall</Typography>
        <Button
          variant="text"
          startIcon={<Iconify icon="eva:play-circle-fill" />}
          sx={{ textTransform: 'none' }}
        >
          Tanıtım Videosunu İzle
        </Button>
      </Stack>

      {/* Günlük Kullanım Adedi Metric */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card
            elevation={0}
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              borderRadius: 2,
              bgcolor: 'background.paper',
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2">{dailyMetric.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Toplam: <strong>{dailyMetric.total}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ortalama: <strong>{dailyMetric.average}</strong>
              </Typography>
            </Box>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                background: 'linear-gradient(135deg, #3461D6 0%, #264697 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Iconify
                icon="eva:layers-outline"
                width={20}
                height={20}
                sx={{ color: '#fff' }}
              />
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* 4 Main Charts */}
      <Grid container spacing={2} sx={{ mt: 3 }}>
        {/* 1 */}
        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{
              p: 2,
              position: 'relative',
              height: 360,
              borderRadius: 2,
              bgcolor: 'background.paper',
            }}
          >
            {renderBadge()}
            <Typography variant="h6" gutterBottom>
              Aylık Teslimat Tercihine Göre Kullanımı
            </Typography>
            <ReactApexChart
              options={monthlyRangeOptions}
              series={monthlyRangeSeries}
              type="bar"
              height={260}
            />
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ mt: 2, gap: 3 }}
            >
              {['Çıkış', 'Kozmetik', 'Elektronik'].map((lbl, i) => (
                <Box key={lbl} display="flex" alignItems="center">
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: monthlyRangeOptions.colors[i],
                      mr: 1,
                    }}
                  />
                  <Typography variant="caption">{lbl}</Typography>
                </Box>
              ))}
            </Box>
          </Card>
        </Grid>
        {/* 2 */}
        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{
              p: 2,
              position: 'relative',
              height: 360,
              borderRadius: 2,
              bgcolor: 'background.paper',
            }}
          >
            {renderBadge()}
            <Typography variant="h6" gutterBottom>
              Aylık Kullanım ve Ciro Hareketleri
            </Typography>
            <ReactApexChart
              options={monthlyComboOptions}
              series={monthlyComboSeries}
              type="line"
              height={260}
            />
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ mt: 2, gap: 3 }}
            >
              {['Kullanım Adedi', 'Ciro'].map((lbl, i) => (
                <Box key={lbl} display="flex" alignItems="center">
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: monthlyComboOptions.colors[i],
                      mr: 1,
                    }}
                  />
                  <Typography variant="caption">{lbl}</Typography>
                </Box>
              ))}
            </Box>
          </Card>
        </Grid>
        {/* 3 */}
        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{
              p: 2,
              position: 'relative',
              height: 360,
              borderRadius: 2,
              bgcolor: 'background.paper',
            }}
          >
            {renderBadge()}
            <Typography variant="h6" gutterBottom>
              Haftalık Easy Mall Hizmeti Kullanımı
            </Typography>
            <ReactApexChart
              options={weeklyDonutOptions}
              series={weeklyDonutSeries}
              type="donut"
              height={260}
            />
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ mt: 2, gap: 3 }}
            >
              {weeklyDonutOptions.labels.map((lbl, i) => (
                <Box key={lbl} display="flex" alignItems="center">
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: weeklyDonutOptions.colors[i],
                      mr: 1,
                    }}
                  />
                  <Typography variant="caption">
                    {`${lbl}: ${weeklyDonutSeries[i]}`}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Card>
        </Grid>
        {/* 4 */}
        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{
              p: 2,
              position: 'relative',
              height: 360,
              borderRadius: 2,
              bgcolor: 'background.paper',
            }}
          >
            {renderBadge()}
            <Typography variant="h6" gutterBottom>
              Haftalık Kullanım ve Ciro Hareketleri
            </Typography>
            <ReactApexChart
              options={weeklyComboOptions}
              series={weeklyComboSeries}
              type="line"
              height={260}
            />
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ mt: 2, gap: 3 }}
            >
              {['Kullanım Adedi', 'Ciro'].map((lbl, i) => (
                <Box key={lbl} display="flex" alignItems="center">
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: weeklyComboOptions.colors[i],
                      mr: 1,
                    }}
                  />
                  <Typography variant="caption">{lbl}</Typography>
                </Box>
              ))}
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Bottom line chart */}
      <Grid container spacing={2} sx={{ mt: 3 }}>
        <Grid item xs={12}>
          <Card
            elevation={0}
            sx={{
              p: 2,
              position: 'relative',
              borderRadius: 2,
              bgcolor: 'background.paper',
            }}
          >
            {renderBadge()}
            <Typography variant="subtitle2">
              Saatlik Kullanım Trafiği ve Satış Hareketleri
            </Typography>
            <ReactApexChart
              options={bottomLineOptions}
              series={bottomLineSeries}
              type="line"
              height={320}
            />
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ mt: 2, gap: 4 }}
            >
              <Box display="flex" alignItems="center">
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: '#3150A0',
                    mr: 1,
                  }}
                />
                <Typography variant="caption">Satış</Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: '#65A6A0',
                    mr: 1,
                  }}
                />
                <Typography variant="caption">Trafik</Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Filter & Table */}
      <Paper sx={{ mt: 3, p: 3, bgcolor: 'background.paper' }} elevation={0}>
        <Grid container spacing={2} alignItems="center">
          {/* Başlangıç ve Bitiş tarihleri yan yana, sola yapışık */}
          <Grid item xs="auto">
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="Başlangıç Tarihi"
                value={startDate}
                onChange={(newVal) => setStartDate(newVal)}
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" />
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs="auto">
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="Bitiş Tarihi"
                value={endDate}
                onChange={(newVal) => setEndDate(newVal)}
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" />
                )}
              />
            </LocalizationProvider>
          </Grid>

          {/* Boşluk bırakarak butonları sağa itiyoruz */}
          <Grid item xs />

          <Grid item xs="auto">
            <Button variant="outlined" size="medium">
              TEMİZLE
            </Button>
          </Grid>
          <Grid item xs="auto">
            <Button variant="contained" size="medium">
              LİSTELE
            </Button>
          </Grid>
        </Grid>

        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ mt: 3, overflowX: 'auto' }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  'NO',
                  'AVM',
                  'KONSİNYER',
                  'SİSTEM',
                  'KONSİNYER KABUL TARİHİ',
                  'SİSTEM KABUL TARİHİ',
                ].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 600, fontSize: 12 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.no} hover>
                  <TableCell>{r.no}</TableCell>
                  <TableCell>{r.avm}</TableCell>
                  <TableCell>{r.konsinyer}</TableCell>
                  <TableCell>{r.sistem}</TableCell>
                  <TableCell>{r.konsinyerKabul}</TableCell>
                  <TableCell>{r.sistemKabul}</TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={6} sx={{ py: 2 }}>
                  <Grid container alignItems="center">
                    <Grid item xs>
                      <Typography variant="caption">
                        Showing 1-5 of 5
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Pagination
                        count={1}
                        page={1}
                        shape="rounded"
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
